import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListTree, Map } from 'lucide-react';
import { AppLayout } from '@/components/Layout/AppLayout';
import { TreeCanvas } from '@/components/Tree/TreeCanvas';
import { MapView } from '@/components/Map/MapView';
import { useTreeStore, type ViewMode } from '@/store/useTreeStore';
import { useTreeFeatures } from '@/hooks/useTreeFeatures';

// ─── View-Toggle ───────────────────────────────────────────────────────────

function ViewToggle() {
  const viewMode = useTreeStore((s) => s.viewMode);
  const setViewMode = useTreeStore((s) => s.setViewMode);
  const { hat } = useTreeFeatures();

  // Ohne Kartenansicht im Tarif ist ein Umschalter mit nur einem Ziel keiner.
  if (!hat('map')) return null;

  const options: { mode: ViewMode; label: string; icon: JSX.Element }[] = [
    {
      mode: 'tree',
      label: 'Stammbaum',
      icon: <ListTree className="h-4 w-4" aria-hidden="true" />,
    },
    {
      mode: 'map',
      label: 'Karte',
      icon: <Map className="h-4 w-4" aria-hidden="true" />,
    },
  ];

  return (
    <div className="absolute right-4 top-4 z-[500] flex rounded-lg border border-slate-200 bg-white shadow-sm">
      {options.map((opt) => (
        <button
          key={opt.mode}
          onClick={() => setViewMode(opt.mode)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors first:rounded-l-lg last:rounded-r-lg ${
            viewMode === opt.mode
              ? 'bg-blue-50 text-blue-700'
              : 'text-slate-500 hover:bg-slate-50'
          }`}
          title={opt.label}
        >
          {opt.icon}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── TreePage ───────────────────────────────────────────────────────────────

export function TreePage() {
  const viewMode = useTreeStore((s) => s.viewMode);
  const setSelectedPerson = useTreeStore((s) => s.setSelectedPerson);
  const [searchParams, setSearchParams] = useSearchParams();

  // ?person=<id> → Person auswählen, Sidebar öffnen und im Baum anspringen.
  // Beide Navbar-Suchen (Schnellsuche und Suche-Seite) landen hier.
  useEffect(() => {
    const personId = searchParams.get('person');
    if (personId) {
      // Sicherstellen, dass wir in der Baumansicht sind
      useTreeStore.getState().setViewMode('tree');
      setSelectedPerson(personId);
      useTreeStore.getState().requestFocus(personId);
      // Parameter entfernen, damit er nicht bei jedem Render erneut feuert
      searchParams.delete('person');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, setSelectedPerson]);

  const hatKarte = useTreeFeatures().hat('map');

  return (
    <AppLayout>
      {/* isolate erzeugt einen eigenen Stacking-Context, damit innere Leaflet-/
         Canvas-Elemente mit hohem z-index die Topbar-Navigation nicht überdecken. */}
      <div className="relative isolate h-full w-full">
        <ViewToggle />
        {/* Der gemerkte Modus kann auf „Karte" stehen, obwohl der Tarif sie
            nicht mehr enthält – dann zurück auf den Baum, statt eine leere
            Fläche zu zeigen. */}
        {viewMode === 'map' && hatKarte ? <MapView /> : <TreeCanvas />}
      </div>
    </AppLayout>
  );
}
